import { useState, useEffect } from 'react';
import { useSettings, useUpdateSettings, useKillSwitch, type Settings } from '../hooks/useApi';

const CATEGORY_LABELS: Record<string, string> = {
  CUSTOM_ORDER: 'Custom Orders',
  PROD_STATUS: 'Production Status',
  SHIPPING: 'Shipping',
  PRODUCT_SUPPORT: 'Product Support',
  WHOLESALE: 'Wholesale',
  FEEDBACK: 'Feedback',
  BOOKING: 'Booking',
  INTERNAL: 'Internal',
};

function KillSwitchToggle({ active, onToggle, isPending }: { active: boolean; onToggle: (val: boolean) => void; isPending: boolean }) {
  const [showConfirm, setShowConfirm] = useState(false);

  const handleClick = () => {
    if (active) {
      // Turning OFF the kill switch (resuming sends) - confirm
      setShowConfirm(true);
    } else {
      // Turning ON the kill switch (stopping sends) - do it immediately
      onToggle(true);
    }
  };

  return (
    <div className={`card border-2 ${active ? 'border-red-300 bg-red-50' : 'border-emerald-300 bg-emerald-50'}`}>
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-gray-900">Global Kill Switch</h3>
          <p className="text-sm text-gray-600">
            {active
              ? 'All auto-sending is STOPPED. Emails will queue for manual review.'
              : 'System is active. Emails meeting thresholds will auto-send.'}
          </p>
        </div>
        <button
          onClick={handleClick}
          disabled={isPending}
          className={`relative inline-flex h-10 w-20 flex-shrink-0 cursor-pointer rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 ${
            active
              ? 'bg-red-500 focus:ring-red-400'
              : 'bg-emerald-500 focus:ring-emerald-400'
          } ${isPending ? 'opacity-50' : ''}`}
        >
          <span
            className={`inline-block h-8 w-8 transform rounded-full bg-white shadow-md transition-transform duration-200 mt-1 ${
              active ? 'translate-x-10' : 'translate-x-1'
            }`}
          />
        </button>
      </div>

      {showConfirm && (
        <div className="mt-4 rounded-lg border border-amber-300 bg-amber-50 p-4">
          <p className="text-sm font-medium text-amber-800">
            Are you sure you want to resume auto-sending? Emails that meet confidence thresholds will be sent automatically.
          </p>
          <div className="mt-3 flex gap-2">
            <button
              onClick={() => {
                onToggle(false);
                setShowConfirm(false);
              }}
              disabled={isPending}
              className="btn-success text-xs"
            >
              Yes, Resume Auto-Send
            </button>
            <button
              onClick={() => setShowConfirm(false)}
              className="btn-outline text-xs"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function SettingsView() {
  const { data: settings, isLoading, isError } = useSettings();
  const updateSettings = useUpdateSettings();
  const killSwitch = useKillSwitch();

  const [localSettings, setLocalSettings] = useState<Settings | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (settings && !localSettings) {
      setLocalSettings(settings);
    }
  }, [settings, localSettings]);

  const handleKillSwitch = (enabled: boolean) => {
    killSwitch.mutate(enabled, {
      onSuccess: () => {
        setLocalSettings((s) => (s ? { ...s, kill_switch: enabled } : s));
      },
    });
  };

  const handleCategoryToggle = (category: string, field: 'auto_send', value: boolean) => {
    setLocalSettings((s) => {
      if (!s) return s;
      return {
        ...s,
        categories: {
          ...s.categories,
          [category]: { ...s.categories[category], [field]: value },
        },
      };
    });
    setSaved(false);
  };

  const handleThresholdChange = (category: string, value: number) => {
    setLocalSettings((s) => {
      if (!s) return s;
      return {
        ...s,
        categories: {
          ...s.categories,
          [category]: { ...s.categories[category], threshold: value },
        },
      };
    });
    setSaved(false);
  };

  const handleFieldChange = (field: keyof Settings, value: number | string) => {
    setLocalSettings((s) => (s ? { ...s, [field]: value } : s));
    setSaved(false);
  };

  const handleSave = () => {
    if (!localSettings) return;
    updateSettings.mutate(
      {
        categories: localSettings.categories,
        daily_cap: localSettings.daily_cap,
        hourly_cap: localSettings.hourly_cap,
        circuit_breaker_limit: localSettings.circuit_breaker_limit,
        disclosure_text: localSettings.disclosure_text,
      },
      {
        onSuccess: () => setSaved(true),
      },
    );
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
      </div>
    );
  }

  if (isError || !localSettings) {
    return (
      <div className="card border-red-200 bg-red-50 text-center text-red-700">
        Failed to load settings.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Kill Switch */}
      <KillSwitchToggle
        active={localSettings.kill_switch}
        onToggle={handleKillSwitch}
        isPending={killSwitch.isPending}
      />

      {/* Per-category settings */}
      <div className="card">
        <h3 className="mb-4 text-sm font-semibold text-gray-900 uppercase tracking-wide">
          Category Auto-Send Rules
        </h3>
        <div className="flex flex-col gap-4">
          {Object.entries(localSettings.categories).map(([category, config]) => (
            <div
              key={category}
              className="flex flex-col gap-2 rounded-lg border border-gray-200 p-4 sm:flex-row sm:items-center sm:gap-6"
            >
              <div className="w-40 flex-shrink-0">
                <span className="text-sm font-medium text-gray-900">
                  {CATEGORY_LABELS[category] ?? category}
                </span>
              </div>

              {/* Toggle */}
              <label className="flex items-center gap-2 cursor-pointer flex-shrink-0">
                <button
                  onClick={() => handleCategoryToggle(category, 'auto_send', !config.auto_send)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    config.auto_send ? 'bg-emerald-500' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                      config.auto_send ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
                <span className="text-xs text-gray-600">Auto-send</span>
              </label>

              {/* Threshold slider */}
              <div className="flex flex-1 items-center gap-3">
                <span className="text-xs text-gray-500 w-16">Threshold</span>
                <input
                  type="range"
                  min="0.50"
                  max="1.00"
                  step="0.01"
                  value={config.threshold}
                  onChange={(e) => handleThresholdChange(category, parseFloat(e.target.value))}
                  className="flex-1 accent-blue-600"
                  disabled={!config.auto_send}
                />
                <span className="text-sm font-medium text-gray-700 w-12 text-right">
                  {(config.threshold * 100).toFixed(0)}%
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Rate limits */}
      <div className="card">
        <h3 className="mb-4 text-sm font-semibold text-gray-900 uppercase tracking-wide">
          Rate Limits
        </h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">Daily Auto-Send Cap</label>
            <input
              type="number"
              min="0"
              className="input"
              value={localSettings.daily_cap}
              onChange={(e) => handleFieldChange('daily_cap', parseInt(e.target.value) || 0)}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">Hourly Auto-Send Cap</label>
            <input
              type="number"
              min="0"
              className="input"
              value={localSettings.hourly_cap}
              onChange={(e) => handleFieldChange('hourly_cap', parseInt(e.target.value) || 0)}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">Circuit Breaker Limit</label>
            <input
              type="number"
              min="0"
              className="input"
              value={localSettings.circuit_breaker_limit}
              onChange={(e) => handleFieldChange('circuit_breaker_limit', parseInt(e.target.value) || 0)}
            />
            <p className="mt-1 text-xs text-gray-400">Consecutive failures before auto-send pauses</p>
          </div>
        </div>
      </div>

      {/* Disclosure text */}
      <div className="card">
        <h3 className="mb-4 text-sm font-semibold text-gray-900 uppercase tracking-wide">
          AI Disclosure
        </h3>
        <label className="mb-1 block text-xs font-medium text-gray-600">
          Disclosure text appended to AI-generated responses
        </label>
        <textarea
          className="input min-h-[80px] resize-y"
          value={localSettings.disclosure_text}
          onChange={(e) => handleFieldChange('disclosure_text', e.target.value)}
        />
      </div>

      {/* Save */}
      <div className="flex items-center gap-3">
        <button
          onClick={handleSave}
          disabled={updateSettings.isPending}
          className="btn-primary"
        >
          {updateSettings.isPending ? 'Saving...' : 'Save Changes'}
        </button>
        {saved && (
          <span className="text-sm font-medium text-emerald-600">Settings saved successfully.</span>
        )}
        {updateSettings.isError && (
          <span className="text-sm font-medium text-red-600">Failed to save. Try again.</span>
        )}
      </div>
    </div>
  );
}
